import type { NoteEditorOverlayContextPayload } from '@components/common/NoteEditorOverlay/notesFeature.types'
import type {
  IDentalChart,
  IPerioChart,
} from '@dataTypes/chartingFeature.types'
import { NoteContextType } from '@dataTypes/note.type'
import type { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'

export type ChartingRouteTab = 'home' | 'perio' | 'treatment-plan' | 'history'

export function getChartingRouteTabFromPathname(
  pathname: string,
): ChartingRouteTab {
  const path = pathname.replace(/\/$/, '')
  if (path.endsWith('/perio')) return 'perio'
  if (path.endsWith('/treatment-plan')) return 'treatment-plan'
  if (path.endsWith('/history')) return 'history'
  return 'home'
}

export type ResolveChartingNoteOverlayPayloadInput = {
  currentTab: ChartingRouteTab
  id: string | null
  openFirstNote?: boolean
  treatmentPlans: ITreatmentPlan[] | null | undefined
  dentalCharts: IDentalChart[]
  perioCharts: IPerioChart[]
  selectedDentalChart: IDentalChart | null
  createDentalChart: IDentalChart | null
  activeDentalChart?: IDentalChart | null
  selectedPerioChart: IPerioChart | null
  createPerioChart: IPerioChart | null
}

/**
 * Builds overlay payload for charting notes (add note, view note, history row actions share this).
 */
export function resolveChartingNoteOverlayPayload(
  input: ResolveChartingNoteOverlayPayloadInput,
): NoteEditorOverlayContextPayload {
  const {
    currentTab,
    id,
    openFirstNote,
    treatmentPlans,
    dentalCharts,
    perioCharts,
    selectedDentalChart,
    createDentalChart,
    activeDentalChart,
    selectedPerioChart,
    createPerioChart,
  } = input

  let contextId: string | undefined
  let contextType: NoteContextType | undefined
  let contextName: string | undefined

  if (currentTab === 'history') {
    if (id) {
      const plan = treatmentPlans?.find((p) => p.tp_id === id)
      contextId = id
      contextType = NoteContextType.TREATMENT_PLAN
      contextName = plan?.tp_heading ?? 'Treatment plan'
    } else if (treatmentPlans != null && treatmentPlans.length > 0) {
      const plan = treatmentPlans[0]
      contextId = plan.tp_id
      contextType = NoteContextType.TREATMENT_PLAN
      contextName = plan.tp_heading ?? 'Treatment plan'
    }
  } else if (currentTab === 'home') {
    contextId =
      (id || selectedDentalChart?.chart_id) ??
      activeDentalChart?.chart_id ??
      createDentalChart?.chart_id ??
      undefined
    if (contextId) {
      contextType = NoteContextType.CHARTING
      const chartFromHistory =
        id && dentalCharts.length > 0
          ? dentalCharts.find((c) => c.chart_id === id)
          : undefined
      const chartForId =
        chartFromHistory ??
        (activeDentalChart?.chart_id === contextId
          ? activeDentalChart
          : selectedDentalChart?.chart_id === contextId
            ? selectedDentalChart
            : createDentalChart?.chart_id === contextId
              ? createDentalChart
              : (activeDentalChart ?? selectedDentalChart ?? createDentalChart))
      contextName =
        chartForId?.title ?? (id ? 'Dental Chart' : undefined) ?? 'Dental Chart'
    }
  } else if (currentTab === 'perio') {
    contextId =
      id ??
      selectedPerioChart?.chart_id ??
      createPerioChart?.chart_id ??
      undefined
    if (contextId) {
      contextType = NoteContextType.PERIO
      const chartFromHistory =
        id && perioCharts.length > 0
          ? perioCharts.find((c) => c.chart_id === id)
          : undefined
      const chartForId =
        chartFromHistory ??
        (selectedPerioChart?.chart_id === contextId
          ? selectedPerioChart
          : createPerioChart?.chart_id === contextId
            ? createPerioChart
            : (selectedPerioChart ?? createPerioChart))
      contextName =
        chartForId?.title ?? (id ? 'Perio Chart' : undefined) ?? 'Perio Chart'
    }
  } else if (
    currentTab === 'treatment-plan' &&
    treatmentPlans != null &&
    treatmentPlans.length > 0
  ) {
    if (id) {
      const plan = treatmentPlans.find((p) => p.tp_id === id)
      contextId = id
      contextType = NoteContextType.TREATMENT_PLAN
      contextName = plan?.tp_heading ?? 'Treatment plan'
    } else {
      const plan = treatmentPlans[0]
      contextId = plan.tp_id
      contextType = NoteContextType.TREATMENT_PLAN
      contextName = plan.tp_heading ?? 'Treatment plan'
    }
  }

  if (id && !contextType) {
    if (treatmentPlans?.some((p) => p.tp_id === id)) {
      const plan = treatmentPlans.find((p) => p.tp_id === id)
      contextId = id
      contextType = NoteContextType.TREATMENT_PLAN
      contextName = plan?.tp_heading ?? 'Treatment Plan'
    } else {
      contextId = id
      contextType = NoteContextType.CHARTING
      const chartFromList = dentalCharts.find((c) => c.chart_id === id)
      contextName = chartFromList?.title ?? 'Dental Chart'
    }
  }

  if (contextId && contextType) {
    return {
      contextId,
      contextType,
      ...(contextName ? { contextName } : {}),
      ...(openFirstNote === true ? { openFirstNote: true } : {}),
    }
  }
  return {}
}
